// src/components/Sidebar.jsx
import React from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Sidebar = () => {
    const { user, isAuthenticated } = useAuth();
    const location = useLocation();

    // Enhanced debug logging
    console.log('User object in sidebar:', user);
    console.log('User role:', user?.data?.role);

    const NavItem = ({ to, icon, children }) => (
        <Nav.Item>
            <Nav.Link
                as={Link}
                to={to}
                active={location.pathname.startsWith(to)}
                className="sidebar-nav-link"
            >
                <div className="d-flex align-items-center">
                    <div className="sidebar-icon">
                        <i className={`bi ${icon}`}></i>
                    </div>
                    <span className="sidebar-text">{children}</span>
                </div>
            </Nav.Link>
        </Nav.Item>
    );

    if (!isAuthenticated || !user) return null;

    // Robust role check with multiple fallbacks
    const getUserRole = () => {
        // Check different possible role properties
        if (typeof user?.data?.role === 'string') {
            return user.data.role.toLowerCase();
        }
        if (typeof user?.role === 'string') {
            return user.role.toLowerCase();
        }
        if (Array.isArray(user.roles)) {
            return user.roles[0]?.toLowerCase() || 'user';
        }
        return 'user';
    };

    const userRole = getUserRole();
    const isUserRole = userRole === 'user';
    const isAdmin = userRole === 'admin';

    console.log('User role detected:', userRole);
    console.log('Is user role (restricted access):', isUserRole);
    console.log('Is admin:', isAdmin);

    return (
        <div className="sidebar">
            <div className="sidebar-header">
                <div className="sidebar-logo">
                    <i className="bi bi-shield-lock"></i>
                </div>
                <h5 className="sidebar-title">WBUOA Admin</h5>
            </div>
            <Nav className="flex-column">
                {/* Dashboard - Show for all users except 'user' role */}
                {!isUserRole && (
                    <NavItem to="/users/dashboard" icon="bi-speedometer2">
                        Dashboard
                    </NavItem>
                )}

                {/* Documents - Show for all users including 'user' role */}
                <NavItem to="/documents" icon="bi-file-earmark-text">
                    Documents
                </NavItem>

                {/* Categories - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/document-categories" icon="bi-folder">
                        Categories
                    </NavItem>
                )}

                {/* Universities - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/universities" icon="bi-building">
                        Universities
                    </NavItem>
                )}

                {/* Committee Management Section */}
                <div className="sidebar-section-header">
                    <small className="text-muted">COMMITTEES</small>
                </div>

                {/* Unit Committee - Show for all users including 'user' role */}
                <NavItem to="/unitcommittee" icon="bi-person-badge">
                    Unit Committee
                </NavItem>

                {/* State Committee - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/statecommittee" icon="bi-people-fill">
                        State Committee
                    </NavItem>
                )}

                {/* Executive Committee - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/executivecommittee" icon="bi-diagram-3">
                        Executive Committee
                    </NavItem>
                )}

                {/* Office Bearer - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/officebearer" icon="bi-award">
                        Office Bearer
                    </NavItem>
                )}

                {/* Secretary - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/secretary" icon="bi-person-vcard">
                        Secretary
                    </NavItem>
                )}

                {/* President - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/president" icon="bi-person-badge-fill">
                        President
                    </NavItem>
                )}

                {/* Content Management Section */}
                <div className="sidebar-section-header">
                    <small className="text-muted">CONTENT</small>
                </div>

                {/* Gallery - Show only for non-'user' roles */}
                {!isUserRole && (
                    <NavItem to="/gallery" icon="bi-images">
                        Gallery
                    </NavItem>
                )}

                {/* News - Available to all authenticated users */}
                <NavItem to="/news" icon="bi-newspaper">
                    News
                </NavItem>

                {/* Admin-only items - Show only for admin roles */}
                {isAdmin && (
                    <>
                        <div className="sidebar-section-header">
                            <small className="text-muted">ADMIN</small>
                        </div>
                        <NavItem to="/users" icon="bi-people">
                            Users
                        </NavItem>
                    </>
                )}

                <div className="sidebar-section-header">
                    <small className="text-muted">ACCOUNT</small>
                </div>
                <NavItem to="/users/update-password" icon="bi-key">
                    Update Password
                </NavItem>
            </Nav>
            <div className="sidebar-footer">
                <div className="sidebar-user">
                    <i className="bi bi-person-circle"></i>
                    <span>{user.emp_name || user.name || user.email}</span>
                    <small className="d-block text-muted">
                        Role: {typeof user.role === 'string'
                            ? user.role
                            : Array.isArray(user.roles)
                                ? user.roles.join(', ')
                                : userRole}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
